import React, { useRef, useState, useCallback } from "react";
import { MapPin, Clock, Phone, ArrowUpRight, ChevronLeft, ChevronRight, WhatsApp } from "./Icons";
import { useReveal } from "../hooks/useReveal";
import { CLINICS, whatsappUrl, consultMessage, formatHours, SAME_PRICE_NOTE } from "../data/clinics";

function PhotoCarousel({ clinic }) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const { photos } = clinic;

  const goTo = useCallback(
    (i) => {
      const track = trackRef.current;
      if (!track) return;
      const next = (i + photos.length) % photos.length;
      track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
    },
    [photos.length]
  );

  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (track) setIndex(Math.round(track.scrollLeft / track.clientWidth));
  }, []);

  const navButtonClass =
    "absolute top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass flex items-center justify-center text-primary " +
    "opacity-0 group-hover/carousel:opacity-100 focus-visible:opacity-100 hover:bg-white transition-all duration-200 cursor-pointer";

  return (
    <div className="group/carousel relative aspect-[4/3] overflow-hidden bg-primary-50">
      {/* Swipeable track (native scroll-snap) */}
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex h-full overflow-x-auto snap-x snap-mandatory scrollbar-none"
      >
        {photos.map((photo) => (
          <img
            key={photo.src + photo.alt}
            src={photo.src}
            alt={photo.alt}
            className="w-full h-full flex-shrink-0 object-cover snap-center"
            loading="lazy"
          />
        ))}
      </div>

      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-primary/50 to-transparent pointer-events-none" />

      {clinic.isNew && (
        <span className="absolute top-4 left-4 badge-accent backdrop-blur-md bg-white/80">
          Now Open
        </span>
      )}

      {photos.length > 1 && (
        <>
          <button onClick={() => goTo(index - 1)} className={`${navButtonClass} left-3`} aria-label="Previous photo">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => goTo(index + 1)} className={`${navButtonClass} right-3`} aria-label="Next photo">
            <ChevronRight size={18} />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((photo, i) => (
              <button
                key={photo.src + photo.alt}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${
                  index === i ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/70"
                }`}
                aria-label={`Show photo ${i + 1} of ${photos.length}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ClinicCard({ clinic, index }) {
  const [ref, isVisible] = useReveal();

  const details = [
    { icon: MapPin, label: "Address", value: clinic.address },
    { icon: Clock, label: "Hours", value: formatHours(clinic) },
    { icon: Phone, label: "WhatsApp", value: clinic.phoneDisplay },
  ];

  return (
    <div
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <article className="card overflow-hidden h-full flex flex-col">
        <PhotoCarousel clinic={clinic} />

        <div className="p-6 sm:p-8 flex-1 flex flex-col">
          <span className="section-label block mb-2">
            Clinic {String(index + 1).padStart(2, "0")}
          </span>
          <h3 className="font-display text-display-sm text-primary mb-6">{clinic.name}</h3>

          <ul className="space-y-4 mb-8">
            {details.map(({ icon: IconComponent, label, value }) => (
              <li key={label} className="flex gap-3 text-sm text-text-muted leading-relaxed">
                <IconComponent size={18} className="text-accent-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  <span className="sr-only">{label}: </span>
                  {value}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-auto flex flex-wrap gap-3">
            <a
              href={whatsappUrl(clinic, consultMessage(clinic))}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary btn-sm"
            >
              <WhatsApp size={16} />
              Chat on WhatsApp
            </a>
            <a
              href={clinic.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline btn-sm"
            >
              Get Directions
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}

export default function Clinics() {
  const [headerRef, headerVisible] = useReveal();

  return (
    <section id="clinics" className="section bg-bg">
      <div className="container-wide mx-auto">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`reveal ${headerVisible ? "visible" : ""} text-center max-w-2xl mx-auto mb-16`}
        >
          <span className="section-label">Our Clinics</span>
          <h2 className="font-display text-display-lg text-primary mb-4 text-balance">
            Two Clinics, One <span className="text-accent-700">Standard</span>
          </h2>
          <p className="text-text-muted leading-relaxed">
            Visit whichever Marvie Beauty clinic is closest to you. {SAME_PRICE_NOTE},
            same personalized care.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {CLINICS.map((clinic, i) => (
            <ClinicCard key={clinic.id} clinic={clinic} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
